import { Fragment } from 'react'

import { useSelector } from 'react-redux'

import AnalyticsAudienceMetricsWrapper from '@components/Analytics/AudienceMetrics'
import AnalyticsPostPerformance from '@components/Analytics/PostPerformance/PostPerformance'
import InfluencerSubmissions from '@components/Inbox/InfluencerIdentity/Submissions/Submissions'
import SubmissionCardFull from '@components/Submission/Card/Full/Full'
import SubmissionInfluencerProfile from '@components/Submission/Influencer/Profile'
import FullLoading from '@components/UI/Full/Loading'
import SinglePageContainer from '@components/UI/SinglePageContainer/SinglePageContainer'
import { ICampaignContextProps } from '@context/Campaign'
import { MultiframeControlsProvider } from '@context/MultiframeControls'
import { ISubmissionContextProps } from '@context/Submission'
import { ISubmissionListFilterContextProps } from '@context/SubmissionListFilter'
import { BrandManagerIdentityProviders, SubmissionStatuses, SupportedSubmissionTypes } from '@enums'
import {
  withCampaignNoRenderBlock,
  withSubmissionListFilter,
  withSubmissionNoRenderBlock,
} from '@hocs'
import { IApplicationState } from '@store'
import {
  selectHasSubmissionPerformanceLink,
  selectIsLoadingSubmissionPage,
  selectIsSubmissionFromInstagram,
  selectIsSubmissionFromSnapchat,
  selectIsSubmissionFromTiktok,
  selectIsSubmissionFromX,
} from '@store/submissions'
import { getSubmissionFlags } from '@utils'
import { isSocialCampaign } from '@utils/campaign'
import SubmissionChatOpenOnLoadWatcher from '../Chat/OpenOnLoadWatcher'
import SubmissionPerformance from '../Performance/Performance'
import SubmissionSummaryNavigationWrapper from './Navigation/NavigationWrapper'
import { SummaryWrapper } from './Summary.styled'

export interface IInternalProps
  extends ICampaignContextProps,
    ISubmissionContextProps,
    ISubmissionListFilterContextProps {}

export const SubmissionSummary = ({
  submission,
  campaign,
  filter,
  isCampaignFetched,
  isSubmissionFetched,
}: IInternalProps) => {
  const isSubmissionAndCampaignFetched = isSubmissionFetched && isCampaignFetched

  const showLoadingWrapper = useSelector((state: IApplicationState) =>
    isSubmissionAndCampaignFetched
      ? selectIsLoadingSubmissionPage(state, campaign.id, filter, submission.id)
      : undefined,
  )
  const isSubmissionFromInstagram = useSelector((state: IApplicationState) =>
    isSubmissionAndCampaignFetched
      ? selectIsSubmissionFromInstagram(state, submission.id)
      : undefined,
  )
  const isSubmissionFromTikTok = useSelector((state: IApplicationState) =>
    isSubmissionAndCampaignFetched ? selectIsSubmissionFromTiktok(state, submission.id) : undefined,
  )
  const isSubmissionFromX = useSelector((state: IApplicationState) =>
    isSubmissionAndCampaignFetched ? selectIsSubmissionFromX(state, submission.id) : undefined,
  )
  const isSubmissionFromSnapchat = useSelector((state: IApplicationState) =>
    isSubmissionAndCampaignFetched
      ? selectIsSubmissionFromSnapchat(state, submission.id)
      : undefined,
  )
  const hasSubmissionPerformanceLink = useSelector((state: IApplicationState) =>
    isSubmissionAndCampaignFetched
      ? selectHasSubmissionPerformanceLink(state, submission.id)
      : undefined,
  )

  const { isShorts } = getSubmissionFlags(submission)

  const isPinterest = submission.identity_provider === BrandManagerIdentityProviders.Pinterest

  const showAudienceMetrics = !isPinterest && !submission.is_proxy && !submission.proxy_submission

  if (showLoadingWrapper || !isSubmissionAndCampaignFetched) {
    return (
      <SinglePageContainer>
        <SubmissionSummaryNavigationWrapper>
          <FullLoading />
        </SubmissionSummaryNavigationWrapper>
      </SinglePageContainer>
    )
  }

  const isSocialSubmission = isSocialCampaign(campaign)
  const submissionType = isSocialSubmission
    ? SupportedSubmissionTypes.SocialSubmission
    : SupportedSubmissionTypes.ContentSubmission

  const isSubmissionPublished = submission!.status === SubmissionStatuses.Published

  const showPerformance = hasSubmissionPerformanceLink && isSubmissionPublished
  const supportAnalytics =
    isSubmissionFromInstagram ||
    isSubmissionFromTikTok ||
    isSubmissionFromX ||
    isSubmissionFromSnapchat ||
    isShorts

  return (
    <SinglePageContainer>
      <SubmissionChatOpenOnLoadWatcher submissionId={submission.id} campaignId={campaign.id} />
      <SummaryWrapper>
        <MultiframeControlsProvider id="submission-card-summary" frames={submission.media_objects}>
          <SubmissionSummaryNavigationWrapper>
            <SubmissionCardFull
              submission={submission!}
              submissionType={submissionType}
              campaign={campaign}
              showLicenseRequestAsApprove={!isSocialSubmission}
            />
          </SubmissionSummaryNavigationWrapper>
        </MultiframeControlsProvider>
      </SummaryWrapper>
      <InfluencerSubmissions />

      {isSocialSubmission && (
        <Fragment>
          {showPerformance && (
            <Fragment>
              {supportAnalytics ? <AnalyticsPostPerformance /> : <SubmissionPerformance />}
            </Fragment>
          )}
          {!isSubmissionPublished && <SubmissionInfluencerProfile />}
        </Fragment>
      )}
      {showAudienceMetrics && (
        <AnalyticsAudienceMetricsWrapper campaignId={campaign.id} submissionId={submission.id} />
      )}
    </SinglePageContainer>
  )
}

export default withSubmissionListFilter(
  withCampaignNoRenderBlock(withSubmissionNoRenderBlock(SubmissionSummary)),
)
